import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import WorkRequestTable from './HVworkPermitTable';
import { Plus, CheckCircle, X } from 'lucide-react';
import ComponentHeader from '../../../components/ComponentHeader';

import { HVWorkPermitProvider } from '../../../components/Providers/CreateHVWorkPermitProvider/CreateHVWorkContext';
import HVWorkPermitModal from '../../../components/CreateHvWorkPermitModal';
import { useSelector } from 'react-redux';

const HVworkPermits = () => {
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const tableRef = useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState('');
  const [successPopupTitle, setSuccessPopupTitle] = useState('');

  // Get user role from Redux
  const role = useSelector((state: any) => state.user?.role);
  const isResponsiblePerson = role === 'responsible_person';

  const handleSearch = (query: string) => {
    setSearchQuery(query);

    if (tableRef.current?.fetchWorkPermits) {
      tableRef.current.fetchWorkPermits(1, query);
    }
  };

  // Check for edit parameter in URL
  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (editParam) {
      setEditId(Number(editParam));
      setOpenModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!showSuccessPopup) return;

    const timer = setTimeout(() => {
      setShowSuccessPopup(false);
    }, 5000); // auto-close after 5 seconds

    return () => clearTimeout(timer);
  }, [showSuccessPopup]);

  const handleModalClose = () => {
    setOpenModal(false);
    setEditId(null);
  };

  // FIX: Handle both Add & Edit messages correctly
  const handleEditSuccess = () => {
    setOpenModal(false);

    if (editId) {
      setSuccessPopupTitle('Edited Successfully');
      setSuccessPopupMessage('Work permit updated successfully.');
    } else {
      setSuccessPopupTitle('Added Successfully');
      setSuccessPopupMessage('Work permit added successfully.');
    }

    setEditId(null);
    setShowSuccessPopup(true);

    if (tableRef.current?.fetchWorkPermits) {
      tableRef.current.fetchWorkPermits(1, searchQuery);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Header title={'HV Work Permits'} />

      <div className="flex-1 overflow-auto">
        <div className="flex justify-between items-center mb-4">
          {/* New HV Work Permit Button */}
          {isResponsiblePerson && (
            <button
              className="flex items-center gap-2 bg-[#e84545] text-white ml-6 px-4 py-2 rounded-md text-sm font-medium mt-6"
              onClick={() => {
                setEditId(null);
                setOpenModal(true);
              }}
            >
              <Plus size={16} /> New HV Work Permit
            </button>
          )}

          {/*  Search works correctly now */}
          <div className="ml-auto">
            <ComponentHeader onSearch={handleSearch} />
          </div>
        </div>

        <WorkRequestTable ref={tableRef} />
      </div>

      <HVWorkPermitProvider>
        <HVWorkPermitModal isOpen={openModal} onClose={handleModalClose} onEditSuccess={handleEditSuccess} editId={editId} />
      </HVWorkPermitProvider>

      {/*  Success Popup */}
      {showSuccessPopup && (
        <div className="fixed bottom-6 right-6 z-[200]">
          <div className="bg-green-500 text-white rounded-lg shadow-xl p-5 min-w-[320px] max-w-[420px] relative">
            {/* Close Button */}
            <button onClick={() => setShowSuccessPopup(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>

            {/* Dynamic Title */}
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <h4 className="font-semibold text-base">{successPopupTitle}</h4>
            </div>

            {/* Message */}
            <p className="text-sm mb-4 pl-7">{successPopupMessage}</p>

            {/* OK Button */}
            <div className="flex justify-center">
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="w-full py-2 bg-white text-green-600 font-semibold rounded-md hover:bg-gray-100 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HVworkPermits;
